/**
 * The main request functionality to make HTTP requests using Axios.
 * @param {object} productData                - Configuration for the HTTP request.
 * @param {Array}  compareProductQueryParams  - Array of current compareProduct query params.
 * @returns {object|undefined}                - Sorted product data based on order of current
 *                                              compareProduct query params if productData and
 *                                              compareProductQueryParams are passed in.
 *                                              Otherwise, returns undefined.
 */
export const sortProductData = (productData, compareProductQueryParams) => {
  // return early if there is no productData or no compareProductQueryParams supplied
  if (!productData || !compareProductQueryParams) {
    return undefined;
  }

  const productIndexMapping = {};
  compareProductQueryParams.forEach((value, index) => {
    productIndexMapping[value] = index;
  });

  // Sort product objects in same order as they are listed in the compareProduct query params
  const sortedProducts = productData.products.sort((a, b) => {
    const indexA = productIndexMapping[compareProductQueryParams.find((value) => a.pid === value)];
    const indexB = productIndexMapping[compareProductQueryParams.find((value) => b.pid === value)];
    return indexA - indexB;
  });

  // Sort the objects in each productData.productSpecs.specGroups.spec.values array in same order
  // as they are listed in the compareProduct query params
  const hasProductSpecGroups = Boolean(productData?.productSpecs?.specGroups.length);

  let formattedProductSpecGroups;
  if (hasProductSpecGroups) {
    formattedProductSpecGroups = productData.productSpecs.specGroups.map((specGroup) => {
      const { specs, ...restOfSpecGroup } = specGroup;

      const updatedSpecs = specs.map((spec) => {
        const { values, ...restOfSpec } = spec;
        const hasValues = Boolean(values?.length);
        let sortedValues;

        if (hasValues) {
          sortedValues = values.sort((a, b) => {
            const indexA = productIndexMapping[compareProductQueryParams.find((value) => a.pid === value)];
            const indexB = productIndexMapping[compareProductQueryParams.find((value) => b.pid === value)];
            return indexA - indexB;
          });
        }

        return {
          values: sortedValues,
          ...restOfSpec,
        };
      });

      return {
        specs: updatedSpecs,
        ...restOfSpecGroup,
      };
    });

    return {
      products: sortedProducts,
      productSpecs: {
        specGroups: formattedProductSpecGroups,
      },
    };
  }

  // If there are no productSpecs, just return sorted products
  return {
    products: sortedProducts,
    productSpecs: null,
  };
};

export default sortProductData;
